import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import 'react-quill/dist/quill.snow.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useSelector, useDispatch } from 'react-redux';

import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { generateEmailSaga } from './saga';
import {
  generateEmailActions,
  reducer,
  sliceKey,
} from 'app/pages/GenerateEmailFromTemplatesPage/slice';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import LoadingIndicator from 'app/components/LoadingIndicator';
import { useStyles } from 'app/pages/GenerateEmailFromTemplatesPage/style';
import {
  selectInventory,
  selectCompiledTemplate,
  selectUploadFile,
  selectTemplates,
  selectCategories,
  selectSubCategories,
  selectSegments,
  selectAlgorithm,
} from 'app/pages/GenerateEmailFromTemplatesPage/selectors';
import './quill.css';
import VehicleSelectionModal from 'app/pages/GenerateEmailFromTemplatesPage/components/VehicleSelectionModal';
import Step1Component from 'app/pages/GenerateEmailFromTemplatesPage/components/Step1';
import Step2Component from 'app/pages/GenerateEmailFromTemplatesPage/components/Step2';
import Step3Component from 'app/pages/GenerateEmailFromTemplatesPage/components/Step3';
import Step4Component from 'app/pages/GenerateEmailFromTemplatesPage/components/Step4';
import Step5Component from 'app/pages/GenerateEmailFromTemplatesPage/components/Step5';
import PreviewComponent from 'app/pages/GenerateEmailFromTemplatesPage/components/PreviewComponent';
import { initialTemplateData } from 'app/pages/GenerateEmailFromTemplatesPage/components/InitialTemplateData';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';

const steps = [
  'Template',
  'Inventory',
  'Text Content',
  'Image Content',
  'CTA Content',
  'Preview',
];

export function GenerateEmailFromTemplatesPage() {
  const classes = useStyles();

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: generateEmailSaga });

  const dispatch = useDispatch();
  const printRef = React.useRef<any>();
  const inventoryButtonRef = React.useRef<any>();
  const selectedDealer = useSelector(selectDealerSelected);
  const selectedDealerId = selectedDealer.dealer || null;
  const inventoryDetails = useSelector(selectInventory);
  const inventory = inventoryDetails.data?.body?.inventories || [];
  const compiledTemplateDetails = useSelector(selectCompiledTemplate);
  const compiledTemplate = compiledTemplateDetails.data.body || '';
  const uploadFilesDetails = useSelector(selectUploadFile);
  const uploadFileURL = uploadFilesDetails.data.body?.url || '';
  const templateDetails = useSelector(selectTemplates);
  const templates = templateDetails.data.body || [];
  const categoriesDetails = useSelector(selectCategories);
  const categories = categoriesDetails.data.body || [];
  const subCategoriesDetails = useSelector(selectSubCategories);
  const subCategories = subCategoriesDetails.data.body || [];
  const segmentsDetails = useSelector(selectSegments);
  const segments = segmentsDetails.data.body || [];
  const vehicleFilterOptionsDetails = useSelector(selectAlgorithm);
  const vehicleFilterOptions =
    vehicleFilterOptionsDetails.data?.body?.algorithms[0] || {};
  const vehicleFilterFieldMapper = vehicleFilterOptions.fieldMapper || [];

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const [openVehicleSelectionModal, setOpenVehicleSelectionModal] =
    React.useState(false);
  const [templateData, setTemplateData] = React.useState(initialTemplateData);
  const [selectionModel, setSelectionModel] = React.useState<any>([]);
  const [selectedVehicles, setSelectedVehicles] = React.useState<any>([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState<any>({});
  const [selectedCategory, setSelectedCategory] = React.useState<any>('');
  const [selectedSubCategory, setSelectedSubCategory] = React.useState<any>('');
  const [selectedSegments, setSelectedSegments] = React.useState<any>('');
  const [templateContentVariables, setTemplateContentVariables] =
    React.useState<any>({});
  const [vehicleFilterState, setVehicleFilterState] = React.useState({});
  const [error, setError] = React.useState({});

  React.useEffect(() => {
    handleDealerSelection();
  }, [selectedDealer]);

  React.useEffect(() => {
    setFileUrl();
  }, [uploadFileURL]);

  React.useEffect(() => {
    getSubCategories();
  }, [selectedCategory]);

  React.useEffect(() => {
    getSegments();
  }, [selectedSubCategory]);

  React.useEffect(() => {
    getTemplates();
  }, [selectedSegments]);

  React.useEffect(() => {
    getTemplateContentVariables();
  }, [selectedTemplate]);

  const handleDealerSelection = () => {
    try {
      if (selectedDealerId) {
        // fetch categories
        dispatch(
          generateEmailActions.requestCategories({
            dealer_id: selectedDealerId,
          }),
        );

        // fetch filter options
        dispatch(
          generateEmailActions.requestAlgorithm({
            dealer_id: selectedDealerId,
          }),
        );
      }
    } catch (error) {
      handleError(error);
    }
  };

  const getSubCategories = () => {
    try {
      if (selectedCategory.length) {
        dispatch(
          generateEmailActions.requestSubCategories({
            dealer_id: selectedDealerId,
            category: selectedCategory,
          }),
        );
        getTemplates();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const getSegments = () => {
    try {
      if (selectedSubCategory.length) {
        dispatch(
          generateEmailActions.requestSegments({
            dealer_id: selectedDealerId,
            category: selectedCategory,
            sub_category: selectedSubCategory,
          }),
        );
        getTemplates();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const getTemplates = () => {
    try {
      if (selectedCategory && selectedCategory.length) {
        dispatch(
          generateEmailActions.requestTemplates({
            dealer_id: selectedDealerId,
            category: selectedCategory,
            sub_category: selectedSubCategory,
            segment: selectedSegments,
          }),
        );
      }
    } catch (error) {
      handleError(error);
    }
  };

  const setFileUrl = () => {
    try {
      if (uploadFileURL.length) {
        setTemplateData({
          ...templateData,
          content_image_url: uploadFileURL,
        });
      }
    } catch (error) {
      handleError(error);
    }
  };

  const isStepOptional = (step: number) => {
    return false;
  };

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let validate = validateSteps();
    if (validate) {
      let newSkipped = skipped;
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setSkipped(newSkipped);
      compileTemplate();
    }
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setSkipped(prevSkipped => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const compileTemplate = () => {
    try {
      let templateDataObject = { ...templateData };
      let templateContentObject = {};

      Object.keys(templateContentVariables).map(key => {
        templateContentObject[key] = formatUnorganisedList(
          templateContentVariables[key],
        );
      });

      templateDataObject = {
        ...templateDataObject,
        ...templateContentObject,
        vehicles: selectedVehicles.length
          ? selectedVehicles
          : templateData.vehicles,
      };

      dispatch(
        generateEmailActions.requestCompileTemplate({
          data: {
            template: selectedTemplate.template || '',
            template_data: templateDataObject,
          },
        }),
      );
    } catch (error) {
      handleError(error);
    }
  };

  const handleOpenVehicleSelectionModal = () =>
    setOpenVehicleSelectionModal(true);

  // const handleCloseVehicleSelectionModal = () =>
  //   setOpenVehicleSelectionModal(false);

  const handleRowSelection = vehilces => {
    try {
      let vehiclesArray: any = [];
      vehilces.map(item => {
        let data = inventory.find(vehicle => vehicle._id === item);

        vehiclesArray.push(data);
      });

      setSelectedVehicles(vehiclesArray);
    } catch (error) {
      handleError(error);
    }
  };

  const handleFileUpload = files => {
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('image', files[i]);
      }
      dispatch(
        generateEmailActions.requestUploadFile({
          body: formData,
        }),
      );
    } catch (error) {
      handleError(error);
    }
  };

  const handleTemplateDownload = () => {
    try {
      const objectUrl = URL.createObjectURL(new Blob([compiledTemplate]));

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `template.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    } catch (error) {
      handleError(error);
    }
  };

  const getTemplateContentVariables = () => {
    try {
      if (selectedTemplate?.template?.trim().length) {
        const regex = /\{\{\{([^{}]+)\}\}\}/g;
        let variables = selectedTemplate.template?.match(regex) || [];

        if (variables.length) {
          variables = variables.map(match => match.slice(3, -3)) || [];
        }

        let textVariables = {};
        for (let i = 0; i < variables.length; i++) {
          let variableSplit = variables[i].split(' ') || [];

          if (variableSplit.length > 1) {
            if (variableSplit[0] === 'text') {
              textVariables[variableSplit[1]] =
                initialTemplateData[variableSplit[1]] || '';
            }
          }
        }

        setTemplateContentVariables(textVariables);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleDynamicComponentChange = (name, value) => {
    try {
      let data = { ...vehicleFilterState };
      // @ts-ignore
      data[name] = value;

      setVehicleFilterState(old => ({
        ...old,
        ...data,
      }));
    } catch (error) {}
  };

  const handleClickGetInventory = () => {
    try {
      let data = {
        algorithmId: vehicleFilterOptions._id,
        data: [],
        dataRules: vehicleFilterState,
        dealer: selectedDealerId,
        no_of_data: 0,
        reuse_data: true,
        staticData: {},
        templateId: selectedTemplate._id,
        templateType: 'vehicles',
        use_selected_data: false,
      };
      dispatch(
        generateEmailActions.requestInventory({
          data,
        }),
      );

      handleOpenVehicleSelectionModal();
    } catch (error) {
      handleError(error);
    }
  };

  const handleClickPDFDownload = async () => {
    const doc = new jsPDF();

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = compiledTemplate;
    document.body.appendChild(tempDiv);

    // Use the html method to add HTML content to the PDF
    doc.html(tempDiv, {
      callback: function (doc) {
        // Save the PDF
        doc.save('template.pdf');
        // Remove the temporary div after rendering
        document.body.removeChild(tempDiv);
      },
      x: 10, // X coordinate for the HTML content
      y: 10, // Y coordinate for the HTML content
      width: 180, // Width of the HTML content
      windowWidth: 1024, // Width of the virtual window for rendering HTML
    });
  };

  const formatUnorganisedList = htmlString => {
    // Create a DOM parser
    let parser = new DOMParser();
    let doc = parser.parseFromString(htmlString, 'text/html');

    // Select all the li elements
    let liElements = doc.querySelectorAll('li');

    if (liElements.length) {
      // Iterate through each li element and wrap its content with a p tag
      liElements.forEach(li => {
        // Create a p element
        let p = document.createElement('p');

        // Move the contents of li to the p element
        while (li.firstChild) {
          p.appendChild(li.firstChild);
        }

        // Append the p element back to the li element
        li.appendChild(p);
      });

      let updatedHtmlString = doc.body.innerHTML;

      // Output the updated HTML string
      console.log(updatedHtmlString);
      return updatedHtmlString;
    }

    return htmlString;
  };

  const validateSteps = () => {
    try {
      let errorObject: any = {};
      let errorMessage = '';
      switch (activeStep) {
        case 0:
          if (!Object.keys(selectedTemplate).length) {
            errorObject.template = true;
            errorMessage = 'Please select a template';
          }
          break;

        case 1:
          if (!selectedVehicles.length) {
            errorObject.vehicles = true;
            errorMessage = 'Please select some inventory';
            inventoryButtonRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
          break;

        default:
          break;
      }
      setError(errorObject);

      if (Object.keys(errorObject).length) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'error',
            message: errorMessage,
          }),
        );
        return false;
      }

      return true;
    } catch (error) {
      console.log(error);
      handleError(error);
    }
  };

  const handleError = error => {
    console.log(error);
    dispatch(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: 'Oops! something went wrong',
      }),
    );
  };

  return (
    <>
      <Helmet>
        <title>Generate Email from Templates</title>
        <meta
          name="description"
          content="Generate Email from Templates - Myautoiq"
        />
      </Helmet>

      {(inventoryDetails.loading ||
        compiledTemplateDetails.loading ||
        uploadFilesDetails.loading ||
        templateDetails.loading ||
        categoriesDetails.loading ||
        subCategoriesDetails.loading ||
        segmentsDetails.loading) && <LoadingIndicator />}

      <div className={classes.root}>
        <Typography variant="h4" gutterBottom component="div">
          Generate Email from Templates
        </Typography>

        {!selectedDealerId ? (
          <Typography
            variant="h4"
            gutterBottom
            component="div"
            className={classes.dealerTypography}
          >
            {!selectedDealerId
              ? `Please select a dealer`
              : `No templates configured for this dealer`}
          </Typography>
        ) : (
          <div style={{ marginTop: '10px' }}>
            <Box sx={{ width: '100%' }}>
              {activeStep === steps.length ? (
                <React.Fragment>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      pt: 2,
                      margin: '0 100px',
                    }}
                  >
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button onClick={handleReset}>Reset</Button>
                  </Box>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      pt: 2,
                      margin: '0 200px',
                    }}
                  >
                    <Button
                      color="inherit"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    {isStepOptional(activeStep) && (
                      <Button
                        color="inherit"
                        onClick={handleSkip}
                        sx={{ mr: 1 }}
                      >
                        Skip
                      </Button>
                    )}
                    <Button onClick={handleNext}>
                      {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                    </Button>
                  </Box>
                </React.Fragment>
              )}
              <Stepper activeStep={activeStep} sx={{ margin: '0 200px' }}>
                {steps.map((label, index) => {
                  const stepProps: { completed?: boolean } = {};
                  const labelProps: {
                    optional?: React.ReactNode;
                  } = {};
                  if (isStepOptional(index)) {
                    labelProps.optional = (
                      <Typography variant="caption">Optional</Typography>
                    );
                  }
                  if (isStepSkipped(index)) {
                    stepProps.completed = false;
                  }
                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              {activeStep === steps.length ? (
                <React.Fragment>
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    All steps completed - you&apos;re finished
                  </Typography>
                </React.Fragment>
              ) : null}

              <Box sx={{ flexGrow: 1, marginTop: '90px' }}>
                <Grid
                  justifyContent={'center'}
                  container
                  spacing={2}
                  columnGap={16}
                  sx={{
                    // marginTop: '80px',
                    minHeight: '650px',
                    marginBottom: '50px',
                    // marginLeft: '200px',
                    // marginRight: '200px',
                    // backgroundColor: 'grey',
                  }}
                >
                  <Grid
                    md={activeStep === steps.length - 1 ? 0 : 4}
                    sx={{ height: 'fit-content' }}
                  >
                    {activeStep === 0 ? (
                      <Step1Component
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                        selectedSubCategory={selectedSubCategory}
                        setSelectedSubCategory={setSelectedSubCategory}
                        selectedSegments={selectedSegments}
                        setSelectedSegments={setSelectedSegments}
                        selectedTemplate={selectedTemplate}
                        setSelectedTemplate={setSelectedTemplate}
                        categories={categories}
                        subCategories={subCategories}
                        segments={segments}
                        templates={templates}
                        error={error}
                      />
                    ) : null}
                    {activeStep === 1 ? (
                      <Step2Component
                        vehicleFilterFieldMapper={vehicleFilterFieldMapper}
                        handleDynamicComponentChange={
                          handleDynamicComponentChange
                        }
                        handleClickGetInventory={handleClickGetInventory}
                        inventoryButtonRef={inventoryButtonRef}
                      />
                    ) : null}
                    {activeStep === 2 ? (
                      <Step3Component
                        templateContentVariables={templateContentVariables}
                        setTemplateContentVariables={
                          setTemplateContentVariables
                        }
                      />
                    ) : null}
                    {activeStep === 3 ? (
                      <Step4Component
                        templateData={templateData}
                        setTemplateData={setTemplateData}
                        handleFileUpload={handleFileUpload}
                      />
                    ) : null}
                    {activeStep === 4 ? (
                      <Step5Component
                        templateData={templateData}
                        setTemplateData={setTemplateData}
                      />
                    ) : null}
                  </Grid>
                  <Grid
                    md={activeStep === steps.length - 1 ? 12 : 4}
                    sx={{
                      display: activeStep > steps.length - 1 ? 'none' : '',
                      // border: '1px solid',
                      // borderColor: 'rgba(0,0,0,0.2)',
                      // borderRadius: '5px',
                    }}
                  >
                    <PreviewComponent
                      activeStep={activeStep}
                      steps={steps}
                      compileTemplate={compileTemplate}
                      handleClickPDFDownload={handleClickPDFDownload}
                      handleTemplateDownload={handleTemplateDownload}
                      printRef={printRef}
                      compiledTemplate={compiledTemplate}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </div>
        )}
      </div>
      <VehicleSelectionModal
        openVehicleSelectionModal={openVehicleSelectionModal}
        setOpenVehicleSelectionModal={setOpenVehicleSelectionModal}
        selectionModel={selectionModel}
        setSelectionModel={setSelectionModel}
        handleRowSelection={handleRowSelection}
        setSelectedVehicles={setSelectedVehicles}
      />
    </>
  );
}
